import React, { useEffect, useState, useContext } from 'react';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header/index';
import Card from 'components/inputs/Card';
import { Row, Col } from 'react-bootstrap';
import TextInput from 'components/inputs/Input';
import Select from 'components/inputs/Select';
import Button from 'components/inputs/Button';
import * as _ from 'lodash';
import { Formik, Form, getIn } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import axios from 'axios';
import { get } from 'utils/api';
import { getMuralArts, getMuralArtStations, createMuralArt, updateMuralArt } from 'api/muralArt';
import { getOfferId, mutedText } from 'view/sessionLevelOffer/SessionLevelOfferList';

const uploadBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  padding: '10px 16px',
  borderRadius: 8,
  border: '1px dashed #4a5a6a',
  color: '#cdd7e0',
  fontSize: 13,
};
const card = { border: '1px solid #2a3a48', borderRadius: 8, padding: 16, marginBottom: 16 };
const thumb = { width: 96, height: 96, objectFit: 'cover', borderRadius: 8 };
const STUDIO_OPTIONS = [
  { label: 'Individual artist', value: 'individual' },
  { label: 'Studio', value: 'studio' },
];
const uploadFile = async (file) => {
  const { data } = await get('/util/fileupload', { name: file.name });
  await axios.put(data.signedRequest, file, { headers: { 'Content-Type': file.type } });
  return data.url;
};
const fieldError = (errors, touched, name) => (getIn(errors, name) && getIn(touched, name) ? getIn(errors, name) : null);

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required('Name is required'),
  artist: Yup.object().shape({
    name: Yup.string().trim().required('Artist name is required'),
  }),
});

const MuralArtForm = () => {
  const { muralArtId } = useParams();
  const { navigateTo } = useContext(NavContext);
  const [muralArt, setMuralArt] = useState({});
  const [uploading, setUploading] = useState(false);
  const [stationOptions, setStationOptions] = useState([]);

  useEffect(() => {
    getMuralArtStations().then((res) => setStationOptions(_.map(res.data, (station) => ({ label: station.name, value: getOfferId(station) }))));
    muralArtId && getMuralArts().then((res) => setMuralArt(_.find(res.data, { id: muralArtId }) || {}));
  }, [muralArtId]);

  const artist = _.get(muralArt, 'artist', {});
  const initialValues = {
    name: muralArt.name || '',
    size: muralArt.size || '',
    place: muralArt.place || '',
    implementedAt: muralArt.implementedAt || '',
    artBio: muralArt.artBio || '',
    images: _.get(muralArt, 'images', []),
    stations: _.map(_.get(muralArt, 'stations', []), getOfferId),
    artist: {
      isStudio: !!artist.isStudio,
      name: artist.name || '',
      designName: artist.designName || '',
      age: artist.age ?? '',
      profilePic: artist.profilePic || '',
      bio: artist.bio || '',
      otherArtists: _.join(artist.otherArtists, ', '),
    },
  };

  const upload = async (input) => {
    const files = [...input.files];
    input.value = '';
    const urls = [];
    setUploading(true);
    try {
      for (const file of files) urls.push(await uploadFile(file));
    } catch (err) {
      toast.error('Image upload failed');
    }
    setUploading(false);
    return urls;
  };

  const onSubmit = (values) => {
    const body = {
      ...values,
      artist: {
        ...values.artist,
        age: values.artist.age === '' ? null : Number(values.artist.age),
        otherArtists: _.compact(_.map(_.split(values.artist.otherArtists, ','), _.trim)),
      },
    };
    return (muralArtId ? updateMuralArt(muralArtId, body) : createMuralArt(body))
      .then(() => {
        toast.success(muralArtId ? 'Mural updated' : 'Mural added');
        navigateTo('/muralArts');
      })
      .catch(() => {});
  };

  return (
    <React.Fragment>
      <LayoutContainer>
        <AdminHeader title={muralArtId ? 'Edit SHWA Mural' : 'Add SHWA Mural'} backTo="/muralArts" />
        <div className="page-content-wrapper scrollable">
          <Card>
            <div className="p-4">
              <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
                {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, isSubmitting }) => {
                  const input = (name, label, props = {}) => (
                    <TextInput
                      label={label}
                      name={name}
                      value={getIn(values, name)}
                      onChange={handleChange}
                      error={fieldError(errors, touched, name)}
                      {...props}
                    />
                  );
                  return (
                    <Form onSubmit={handleSubmit}>
                      <div className="section-heading mb-3">Mural details</div>
                      <Row>
                        <Col lg={6}>{input('name', 'Name', { isRequired: true })}</Col>
                        <Col lg={6}>{input('place', 'Place (Optional)')}</Col>
                        <Col lg={3}>{input('size', 'Size (Optional)', { placeholder: 'e.g. 20 x 10 ft' })}</Col>
                        <Col lg={3}>{input('implementedAt', 'Implemented At (Optional)', { placeholder: 'e.g. March 2026' })}</Col>
                        <Col lg={6}>
                          <Select
                            isMulti
                            label="Stations (Optional)"
                            placeholder="Select stations that show this mural"
                            options={stationOptions}
                            name="stations"
                            value={values.stations}
                            onChange={(val) => setFieldValue('stations', val || [])}
                          />
                        </Col>
                        <Col lg={12}>{input('artBio', 'Art Bio (Optional)', { as: 'textarea', rows: 3 })}</Col>
                      </Row>

                      <div style={card}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div className="section-heading mb-0">Images (Optional)</div>
                          <label htmlFor="muralImages" style={uploadBtn}>
                            {uploading ? 'Uploading...' : '+ Upload images'}
                            <input
                              id="muralImages"
                              type="file"
                              accept="image/*"
                              multiple
                              hidden
                              onChange={(e) => upload(e.target).then((urls) => setFieldValue('images', [...values.images, ...urls]))}
                            />
                          </label>
                        </div>
                        <div className="d-flex flex-wrap" style={{ gap: 12 }}>
                          {_.map(values.images, (src, index) => (
                            <div key={src} className="text-center">
                              <img src={src} alt={`mural ${index + 1}`} style={thumb} />
                              <div>
                                <Button className="user-group-delete-Btn mt-2" onClick={() => setFieldValue('images', _.without(values.images, src))}>
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                          {_.isEmpty(values.images) && <span style={mutedText}>No images yet</span>}
                        </div>
                      </div>

                      <div style={card}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div className="section-heading mb-0">Artist</div>
                          <label htmlFor="artistPic" style={uploadBtn}>
                            {values.artist.profilePic ? 'Change profile picture' : '+ Upload profile picture'}
                            <input
                              id="artistPic"
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={(e) => upload(e.target).then(([url]) => url && setFieldValue('artist.profilePic', url))}
                            />
                          </label>
                        </div>
                        <Row>
                          <Col lg={3}>
                            <Select
                              label="Artist Type"
                              options={STUDIO_OPTIONS}
                              disableSort
                              value={values.artist.isStudio ? 'studio' : 'individual'}
                              onChange={(val) => setFieldValue('artist.isStudio', val === 'studio')}
                            />
                          </Col>
                          <Col lg={3}>{input('artist.name', values.artist.isStudio ? 'Studio Name' : 'Artist Name', { isRequired: true })}</Col>
                          <Col lg={3}>{input('artist.designName', 'Design Name (Optional)')}</Col>
                          <Col lg={3}>{input('artist.age', 'Age (Optional)', { type: 'number' })}</Col>
                          <Col lg={9}>{input('artist.otherArtists', 'Other Artists (Optional, comma separated)')}</Col>
                          <Col lg={3} className="text-center">
                            {values.artist.profilePic && (
                              <img src={values.artist.profilePic} alt="artist" style={{ ...thumb, borderRadius: '50%' }} />
                            )}
                          </Col>
                          <Col lg={12}>{input('artist.bio', 'Artist Bio (Optional)', { as: 'textarea', rows: 3 })}</Col>
                        </Row>
                      </div>

                      <div className="d-flex justify-content-end mt-4" style={{ gap: 12 }}>
                        <Button className="cancel_btn" onClick={() => navigateTo('/muralArts')}>
                          Cancel
                        </Button>
                        <Button type="submit" className="save-btn" disabled={isSubmitting || uploading}>
                          {isSubmitting ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </Card>
        </div>
      </LayoutContainer>
    </React.Fragment>
  );
};

export default MuralArtForm;
